import { useEffect, useRef } from 'react';

import {
  isEditableField,
  scrollFocusedInputIntoView,
} from '@/core/lib/scroll-focused-input-into-view';

type IonKeyboardDidShowEvent = CustomEvent<{ keyboardHeight: number }>;

/**
 * Scrolls plain HTML inputs above the keyboard inside `ion-content`.
 *
 * Runs once per keyboard-open (not on every focus/viewport event) to avoid
 * compounding scroll deltas. Actual keyboard height comes from
 * `ionKeyboardDidShow`, which Ionic bridges from the native plugin.
 */
export function useKeyboardAwareInputScroll(): void {
  const keyboardHeightRef = useRef(0);
  const scrollTimerRef = useRef<number | null>(null);
  const scrollingRef = useRef(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const cancelPendingScroll = () => {
      if (scrollTimerRef.current !== null) {
        clearTimeout(scrollTimerRef.current);
        scrollTimerRef.current = null;
      }
    };

    const scrollActiveFieldOnce = async () => {
      const active = document.activeElement;
      if (!(active instanceof HTMLElement) || !isEditableField(active)) return;
      if (keyboardHeightRef.current <= 0) return;
      if (scrollingRef.current) return;

      scrollingRef.current = true;
      try {
        await scrollFocusedInputIntoView(active, keyboardHeightRef.current);
      } finally {
        scrollingRef.current = false;
      }
    };

    const scheduleScroll = () => {
      cancelPendingScroll();
      scrollTimerRef.current = window.setTimeout(() => {
        scrollTimerRef.current = null;
        void scrollActiveFieldOnce();
      }, 120);
    };

    const onKeyboardDidShow = (event: Event) => {
      const { keyboardHeight = 0 } =
        (event as IonKeyboardDidShowEvent).detail ?? {};
      keyboardHeightRef.current = Math.max(0, Math.round(keyboardHeight));
      scheduleScroll();
    };

    const onKeyboardDidHide = () => {
      cancelPendingScroll();
      keyboardHeightRef.current = 0;
      scrollingRef.current = false;
    };

    const onFocusIn = (event: FocusEvent) => {
      const target = event.target;
      if (!(target instanceof HTMLElement) || !isEditableField(target)) return;
      // Keyboard already open: user moved between fields.
      if (keyboardHeightRef.current > 0) {
        scheduleScroll();
      }
    };

    window.addEventListener('ionKeyboardDidShow', onKeyboardDidShow);
    window.addEventListener('ionKeyboardDidHide', onKeyboardDidHide);
    document.addEventListener('focusin', onFocusIn, true);

    return () => {
      cancelPendingScroll();
      window.removeEventListener('ionKeyboardDidShow', onKeyboardDidShow);
      window.removeEventListener('ionKeyboardDidHide', onKeyboardDidHide);
      document.removeEventListener('focusin', onFocusIn, true);
      keyboardHeightRef.current = 0;
      scrollingRef.current = false;
    };
  }, []);
}
